// Badges. Tier (UX §6.3, bottom-right, 34% of the cell, ≥ 16 CSS px): T1–T4 = 1–4 ink-outlined pips in
// the tier colour on a cream disc; T5–T8 = a t-pointed star in the tier colour with the numeral as a vector
// glyph. Shape + count reads without colour, and adjacent tiers always differ in pip or point count.
// Stall (§0.5, UX §6.5; top-right, 36%, ≥ 18 CSS px): the class sets the shape (error circle, wait rounded
// square, paused hexagon; the Gallery's gun-limited diamond) and stalls.json names the glyph. M10: the
// Gallery's "No ammo" state badge (UX §5 GalleryDry, §6.5 Waiting: an empty tin) on the wait square.
import type { Ctx2D } from './ctx.ts';
import { drawGlyphs } from './glyphs.ts';
import type { ArtPalette } from './palette.ts';
import { detailStroke, fillP } from './passes.ts';
import {
  circle,
  ellipse,
  line,
  type PathFn,
  poly,
  regularPts,
  roundPoly,
  rrect,
  starPts,
} from './shapes.ts';
import type { Generator, StallBadgeSpec, TierBadgeSpec } from './types.ts';

/** Pip centres for n = 1..4 (unit disc coordinates). */
const PIPS: readonly (readonly (readonly [number, number])[])[] = [
  [[0, 0]],
  [
    [-0.36, 0],
    [0.36, 0],
  ],
  [
    [0, -0.36],
    [-0.36, 0.26],
    [0.36, 0.26],
  ],
  [
    [-0.34, -0.34],
    [0.34, -0.34],
    [-0.34, 0.34],
    [0.34, 0.34],
  ],
];

/** Space kept free inside the badge frame, as a share of its side (no ink is cut by the frame edge). */
export const BADGE_MARGIN = 0.02;
/** The T5+ star's reach over r: outer point 1.12 r plus its 0.04 r downward shift. */
const STAR_REACH = 1.16;
/** Ink ring width over r (the M8 design: 0.075 of the frame at r = 0.44 of it). */
const INK_RATIO = 0.075 / 0.44;

/**
 * Badge scale: the frame side in units is 100·w; the ink ring (T1–T4: disc r + ow; T5+: star points +
 * half the 2·ow stroke) ends BADGE_MARGIN inside the frame edge, so trimming and extrude copy no cut edge
 * (review fix, M8 merge: the ring reached 0.515 of the frame and was clipped).
 */
export const tierBadgeGen: Generator<TierBadgeSpec> = (c, spec, px, env) => {
  const { pal } = env;
  const S = spec.w * 100;
  c.save();
  c.scale(px / 100, px / 100);
  const cx = S / 2;
  const cy = S / 2;
  const ink = pal.hex('ui.ink');
  // the M8 proportions (ink ring = INK_RATIO of r), scaled so the ring ends BADGE_MARGIN inside the frame
  const reach = S * (0.5 - BADGE_MARGIN);
  const r = reach / ((spec.tier <= 4 ? 1 : STAR_REACH) + INK_RATIO);
  const ow = r * INK_RATIO;
  const tier = pal.sw(pal.tier(spec.tier));
  if (spec.tier <= 4) {
    fillP(c, circle(cx, cy, r + ow), ink);
    fillP(c, circle(cx, cy, r), pal.hex('ui.cream'));
    fillP(c, circle(cx, cy + r * 0.35, r * 0.62), pal.hex('ui.cream2'));
    fillP(c, circle(cx, cy, r * 0.8), pal.hex('ui.cream'));
    const pr = spec.tier === 1 ? r * 0.36 : r * 0.25;
    for (const [dx, dy] of PIPS[spec.tier - 1] ?? []) {
      fillP(c, circle(cx + dx * r, cy + dy * r, pr + ow * 0.8), ink);
      fillP(c, circle(cx + dx * r, cy + dy * r, pr), tier.base);
      fillP(c, circle(cx + dx * r - pr * 0.3, cy + dy * r - pr * 0.3, pr * 0.35), tier.hi);
    }
  } else {
    const star = roundPoly(starPts(cx, cy + r * 0.04, r * 1.12, r * 0.66, spec.tier), r * 0.1);
    c.beginPath();
    star(c);
    c.strokeStyle = ink;
    c.lineWidth = ow * 2;
    c.lineJoin = 'round';
    c.stroke();
    fillP(c, star, tier.base);
    c.save();
    c.beginPath();
    star(c);
    c.clip();
    fillP(c, rrect(0, cy + r * 0.35, S, S, 0), tier.shade);
    c.restore();
    drawGlyphs(
      c,
      String(spec.tier),
      cx,
      cy + r * 0.08,
      r * 0.78,
      pal.hex('ui.cream'),
      ink,
      r * 0.2,
    );
  }
  c.restore();
};

function shapePath(shape: StallBadgeSpec['shape'], cx: number, cy: number, r: number): PathFn {
  if (shape === 'circle') return circle(cx, cy, r);
  if (shape === 'roundedSquare')
    return rrect(cx - r * 0.9, cy - r * 0.9, r * 1.8, r * 1.8, r * 0.42);
  if (shape === 'hexagon') return roundPoly(regularPts(cx, cy, r * 1.02, 6, -90), r * 0.14);
  // the diamond's points stay inside the frame with the 0.12·S ink stroke (r 0.4·S · 1.05 + 0.06·S < 0.5·S)
  return roundPoly(regularPts(cx, cy, r * 1.05, 4, -90), r * 0.14);
}

/** Stall glyphs, drawn in cream over an ink under-stroke, in a unit box centred on (0, 0) of radius 1. */
function glyph(c: Ctx2D, name: string, pal: ArtPalette, r: number): void {
  const cream = pal.hex('ui.cream');
  const ink = pal.hex('ui.ink');
  const w = r * 0.16;
  const strokeBoth = (p: PathFn) => {
    detailStroke(c, p, ink, w * 2.3);
    detailStroke(c, p, cream, w);
  };
  const fillBoth = (p: PathFn) => {
    detailStroke(c, p, ink, w * 1.3);
    fillP(c, p, cream);
  };
  const s = r;
  switch (name) {
    case 'arrowWall':
      strokeBoth(
        line([
          [-0.55 * s, 0],
          [0.22 * s, 0],
        ]),
      );
      strokeBoth(
        line([
          [-0.02 * s, -0.26 * s],
          [0.24 * s, 0],
          [-0.02 * s, 0.26 * s],
        ]),
      );
      strokeBoth(
        line([
          [0.52 * s, -0.5 * s],
          [0.52 * s, 0.5 * s],
        ]),
      );
      break;
    case 'flameSlash':
      fillBoth((cc) => {
        cc.moveTo(0, -0.58 * s);
        cc.bezierCurveTo(0.42 * s, -0.12 * s, 0.4 * s, 0.5 * s, 0, 0.5 * s);
        cc.bezierCurveTo(-0.4 * s, 0.5 * s, -0.42 * s, -0.06 * s, -0.1 * s, -0.2 * s);
        cc.quadraticCurveTo(-0.08 * s, 0.02 * s, 0.04 * s, 0.06 * s);
        cc.quadraticCurveTo(0.08 * s, -0.3 * s, 0, -0.58 * s);
        cc.closePath();
      });
      strokeBoth(
        line([
          [-0.52 * s, 0.52 * s],
          [0.52 * s, -0.52 * s],
        ]),
      );
      break;
    case 'neqPips':
      fillBoth(circle(-0.46 * s, 0, 0.17 * s));
      fillBoth(circle(0.46 * s, 0, 0.17 * s));
      strokeBoth(
        line([
          [-0.16 * s, -0.12 * s],
          [0.16 * s, -0.12 * s],
        ]),
      );
      strokeBoth(
        line([
          [-0.16 * s, 0.12 * s],
          [0.16 * s, 0.12 * s],
        ]),
      );
      strokeBoth(
        line([
          [0.12 * s, -0.34 * s],
          [-0.12 * s, 0.34 * s],
        ]),
      );
      break;
    case 'boxBang':
      detailStroke(c, rrect(-0.5 * s, -0.36 * s, s, 0.84 * s, 0.1 * s), ink, w * 2.3);
      detailStroke(c, rrect(-0.5 * s, -0.36 * s, s, 0.84 * s, 0.1 * s), cream, w);
      drawGlyphs(c, '!', 0, 0.06 * s, 0.56 * s, cream, ink, w * 0.9);
      break;
    case 'drillQ':
      fillBoth(
        poly([
          [-0.16 * s, -0.55 * s],
          [0.16 * s, -0.55 * s],
          [0.16 * s, 0.05 * s],
          [0, 0.4 * s],
          [-0.16 * s, 0.05 * s],
        ]),
      );
      drawGlyphs(c, '?', 0.46 * s, 0.24 * s, 0.5 * s, cream, ink, w * 0.8);
      break;
    case 'cramped':
      for (const d of [-1, 1]) {
        strokeBoth(
          line([
            [d * 0.56 * s, 0],
            [d * 0.18 * s, 0],
          ]),
        );
        strokeBoth(
          line([
            [d * 0.36 * s, -0.2 * s],
            [d * 0.16 * s, 0],
            [d * 0.36 * s, 0.2 * s],
          ]),
        );
      }
      break;
    case 'itemIcon':
      fillBoth(circle(0, 0.06 * s, 0.34 * s));
      drawGlyphs(c, '?', 0, 0.06 * s, 0.42 * s, ink, cream, w * 0.55);
      break;
    case 'blueprint':
      detailStroke(c, rrect(-0.46 * s, -0.46 * s, 0.92 * s, 0.92 * s, 0.1 * s), ink, w * 2.3);
      detailStroke(c, rrect(-0.46 * s, -0.46 * s, 0.92 * s, 0.92 * s, 0.1 * s), cream, w);
      strokeBoth(
        line([
          [-0.22 * s, 0.2 * s],
          [0.22 * s, 0.2 * s],
        ]),
      );
      strokeBoth(
        line([
          [-0.22 * s, -0.12 * s],
          [0.08 * s, -0.12 * s],
        ]),
      );
      break;
    case 'pause':
      fillBoth(rrect(-0.38 * s, -0.44 * s, 0.26 * s, 0.88 * s, 0.08 * s));
      fillBoth(rrect(0.12 * s, -0.44 * s, 0.26 * s, 0.88 * s, 0.08 * s));
      break;
    case 'gunSlash':
      fillBoth(
        roundPoly(
          [
            [-0.54 * s, -0.24 * s],
            [0.5 * s, -0.24 * s],
            [0.5 * s, 0.04 * s],
            [-0.06 * s, 0.04 * s],
            [-0.14 * s, 0.46 * s],
            [-0.44 * s, 0.46 * s],
            [-0.34 * s, 0.04 * s],
            [-0.54 * s, 0.04 * s],
          ],
          0.06 * s,
        ),
      );
      strokeBoth(
        line([
          [-0.56 * s, 0.56 * s],
          [0.56 * s, -0.56 * s],
        ]),
      );
      break;
    case 'gunUp':
      fillBoth(
        roundPoly(
          [
            [-0.54 * s, 0.02 * s],
            [0.3 * s, 0.02 * s],
            [0.3 * s, 0.28 * s],
            [-0.14 * s, 0.28 * s],
            [-0.2 * s, 0.56 * s],
            [-0.44 * s, 0.56 * s],
            [-0.36 * s, 0.28 * s],
            [-0.54 * s, 0.28 * s],
          ],
          0.06 * s,
        ),
      );
      strokeBoth(
        line([
          [0.36 * s, -0.2 * s],
          [0.36 * s, -0.62 * s],
        ]),
      );
      strokeBoth(
        line([
          [0.16 * s, -0.42 * s],
          [0.36 * s, -0.62 * s],
          [0.56 * s, -0.42 * s],
        ]),
      );
      break;
    case 'press':
      fillBoth(rrect(-0.36 * s, -0.5 * s, 0.72 * s, 0.26 * s, 0.06 * s));
      fillBoth(rrect(-0.1 * s, -0.24 * s, 0.2 * s, 0.4 * s, 0.04 * s));
      fillBoth(rrect(-0.5 * s, 0.24 * s, s, 0.24 * s, 0.06 * s));
      break;
    case 'booth':
      fillBoth(rrect(-0.5 * s, -0.5 * s, s, 0.3 * s, 0.1 * s));
      detailStroke(c, rrect(-0.42 * s, -0.2 * s, 0.84 * s, 0.66 * s, 0.06 * s), ink, w * 2.3);
      detailStroke(c, rrect(-0.42 * s, -0.2 * s, 0.84 * s, 0.66 * s, 0.06 * s), cream, w);
      break;
    case 'emptyTin': {
      // an open tin can (UX §6.5 Waiting "No ammo"), outlined, with a 0 on its side
      const can = rrect(-0.4 * s, -0.34 * s, 0.8 * s, 0.86 * s, 0.12 * s);
      detailStroke(c, can, ink, w * 2.3);
      detailStroke(c, can, cream, w);
      const rim = ellipse(0, -0.34 * s, 0.4 * s, 0.15 * s);
      detailStroke(c, rim, ink, w * 2.3);
      detailStroke(c, rim, cream, w);
      drawGlyphs(c, '0', 0, 0.12 * s, 0.44 * s, cream, ink, w * 0.8);
      break;
    }
    default:
      throw new Error(`art: unknown stall glyph "${name}"`);
  }
}

export const stallBadgeGen: Generator<StallBadgeSpec> = (c, spec, px, env) => {
  const { pal } = env;
  const S = spec.w * 100;
  c.save();
  c.scale(px / 100, px / 100);
  const cx = S / 2;
  const cy = S / 2;
  const r = S * 0.4;
  const ink = pal.hex('ui.ink');
  const col = pal.sw(spec.color);
  const p = shapePath(spec.shape, cx, cy, r);
  c.beginPath();
  p(c);
  c.strokeStyle = ink;
  c.lineWidth = S * 0.12;
  c.lineJoin = 'round';
  c.stroke();
  fillP(c, p, col.base);
  c.save();
  c.beginPath();
  p(c);
  c.clip();
  fillP(c, rrect(0, cy + r * 0.45, S, S, 0), col.shade);
  c.restore();
  c.translate(cx, cy);
  glyph(c, spec.glyph, pal, r * 0.78);
  c.restore();
};
